/**
 * AG-UI Lab — standalone, flag-adjacent test surface (parallel; touches nothing).
 *
 * A thin AG-UI consumer (no CopilotKit — proves the protocol itself) so you
 * can exercise everything the bridge does end-to-end in a browser:
 *   • mock/echo    → streaming assistant text
 *   • mock/bash    → tool call + result (atomic args)
 *   • mock/stream  → STREAMING tool args = watch the SQL type itself in
 *   • mock/question→ HITL: papan_question, answer it, agent resumes
 *
 * Reachable at /agui-lab (no nav link — discover by URL). Uses the existing
 * session cookie. ChatPanel is untouched.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { getApiBase } from '@papan/api-client'

type Ev = { type: string; [k: string]: unknown }
type Tool = { name: string; args: string; result?: string }
type Hitl = { kind: 'question' | 'permission'; req: Record<string, unknown> }

const SCENARIOS = ['mock/stream', 'mock/echo', 'mock/bash', 'mock/question']

export function AguiLabPage() {
  const [workspaces, setWorkspaces] = useState<{ id: string; name: string }[]>([])
  const [wsId, setWsId] = useState('')
  const [model, setModel] = useState('mock/stream')
  const [prompt, setPrompt] = useState('write me a query for top regions by sales')
  const [status, setStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle')
  const [log, setLog] = useState<string[]>([])
  const [text, setText] = useState('')
  const [tools, setTools] = useState<Record<string, Tool>>({})
  const [hitl, setHitl] = useState<Hitl | null>(null)
  const sessionRef = useRef('')
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    fetch(`${getApiBase()}/api/workspaces`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        const a = (d?.workspaces ?? []) as { id: string; name: string }[]
        setWorkspaces(a)
        if (a[0]) setWsId(a[0].id)
      })
      .catch(() => {})
    return () => abortRef.current?.abort()
  }, [])

  const reply = useCallback(async (path: string, body: unknown) => {
    await fetch(`${getApiBase()}${path}`, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
    setHitl(null) // stream continues; worker resumes and more events arrive
  }, [])

  const run = useCallback(async () => {
    if (!wsId) return
    setStatus('running'); setLog([]); setText(''); setTools({}); setHitl(null)
    abortRef.current?.abort()
    try {
      const cr = await fetch(`${getApiBase()}/api/agent/sessions`, {
        method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
        // never auto mode: this page shows no ⚡ chip, and it exists to exercise the ask (HITL) path
        body: JSON.stringify({ workspaceId: wsId, model, title: 'agui-lab', autoMode: false }),
      })
      if (!cr.ok) { const msg = await cr.text().catch(() => ''); setStatus('error'); setLog(l => [...l, `session create ${cr.status}: ${msg}`]); return }
      const sid = (await cr.json()).id as string
      sessionRef.current = sid
      const ctrl = new AbortController(); abortRef.current = ctrl
      const res = await fetch(`${getApiBase()}/api/agent/agui`, {
        method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
        body: JSON.stringify({
          threadId: sid, runId: `lab-${Date.now()}`, state: {},
          messages: [{ id: `u${Date.now()}`, role: 'user', content: prompt }],
          tools: [], context: [], forwardedProps: { workspaceId: wsId, model },
        }),
        signal: ctrl.signal,
      })
      if (!res.ok || !res.body) { const msg = await res.text().catch(() => ''); setStatus('error'); setLog(l => [...l, `agui ${res.status}: ${msg}`]); return }
      const rd = res.body.getReader()
      const dec = new TextDecoder()
      let buf = ''
      for (;;) {
        const { done, value } = await rd.read()
        if (done) break
        buf += dec.decode(value, { stream: true })
        const frames = buf.split('\n\n')
        buf = frames.pop() ?? ''
        for (const f of frames) {
          if (f.replace(/^\s+/, '').startsWith(':')) continue // heartbeat comment
          const dl = f.split('\n').find(l => l.startsWith('data:'))
          if (!dl) continue
          let ev: Ev
          try { ev = JSON.parse(dl.slice(5).trim()) } catch { continue }
          const tcn = typeof ev.toolCallName === 'string' ? ev.toolCallName : ''
          setLog(l => [...l, ev.type + (tcn ? ` · ${tcn}` : '')])
          if (ev.type === 'TEXT_MESSAGE_CONTENT') setText(t => t + (ev.delta as string ?? ''))
          if (ev.type === 'TOOL_CALL_START') {
            const id = ev.toolCallId as string
            setTools(s => ({ ...s, [id]: { name: tcn || '?', args: '' } }))
          }
          if (ev.type === 'TOOL_CALL_ARGS') {
            const id = ev.toolCallId as string
            setTools(s => ({ ...s, [id]: { ...(s[id] ?? { name: '?', args: '' }), args: (s[id]?.args ?? '') + (ev.delta as string ?? '') } }))
          }
          if (ev.type === 'TOOL_CALL_END') {
            const id = ev.toolCallId as string
            setTools(s => {
              const t = s[id]
              if (t && (t.name === 'papan_question' || t.name === 'papan_permission')) {
                try {
                  const req = JSON.parse(t.args)
                  setHitl({ kind: t.name === 'papan_question' ? 'question' : 'permission', req })
                } catch { /* ignore */ }
              }
              return s
            })
          }
          if (ev.type === 'TOOL_CALL_RESULT') {
            const id = ev.toolCallId as string
            setTools(s => ({ ...s, [id]: { ...(s[id] ?? { name: '?', args: '' }), result: ev.content as string } }))
          }
          if (ev.type === 'RUN_FINISHED') setStatus('done')
          if (ev.type === 'RUN_ERROR') { setStatus('error'); setLog(l => [...l, `RUN_ERROR: ${ev.message as string ?? ''}`]) }
        }
      }
      setStatus(s => (s === 'running' ? 'done' : s))
    } catch (e) {
      setStatus('error'); setLog(l => [...l, `exception: ${String(e)}`])
    }
  }, [wsId, model, prompt])

  const box: React.CSSProperties = { background: '#0d1729', border: '1px solid #27344b', borderRadius: 8, padding: 12, overflow: 'auto' }
  const mono = '"JetBrains Mono", ui-monospace, Menlo, monospace'

  return (
    <div style={{ minHeight: '100vh', background: '#0F172A', color: '#F8FAFC', fontFamily: 'system-ui, sans-serif', padding: 24 }}>
      <h1 style={{ fontFamily: mono, fontSize: 18, margin: '0 0 4px' }}>AG-UI Lab <span style={{ color: '#22C55E' }}>· parallel test surface</span></h1>
      <p style={{ color: '#94A3B8', fontSize: 13, margin: '0 0 18px' }}>Thin AG-UI consumer. Pick a scenario, Run, watch the protocol. Touches nothing in the real app.</p>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <select value={wsId} onChange={e => setWsId(e.target.value)} style={{ background: '#1E293B', color: '#F8FAFC', border: '1px solid #3b4a63', borderRadius: 6, padding: '7px 10px' }}>
          {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <select value={model} onChange={e => setModel(e.target.value)} style={{ background: '#1E293B', color: '#F8FAFC', border: '1px solid #3b4a63', borderRadius: 6, padding: '7px 10px', fontFamily: mono, fontSize: 13 }}>
          {SCENARIOS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={prompt} onChange={e => setPrompt(e.target.value)} style={{ flex: 1, minWidth: 240, background: '#0d1729', color: '#F8FAFC', border: '1px solid #3b4a63', borderRadius: 6, padding: '7px 10px' }} />
        <button onClick={run} disabled={status === 'running'} style={{ fontFamily: mono, fontSize: 13, cursor: status === 'running' ? 'not-allowed' : 'pointer', border: '1px solid #1f7a44', background: status === 'running' ? '#13241a' : '#16261d', color: '#22C55E', padding: '8px 18px', borderRadius: 6 }}>
          {status === 'running' ? 'running…' : 'Run'}
        </button>
        <span style={{ fontFamily: mono, fontSize: 12, color: status === 'error' ? '#EF4444' : status === 'done' ? '#22C55E' : '#94A3B8' }}>● {status}</span>
      </div>

      {hitl && (
        <div style={{ ...box, border: '1px solid #F59E0B', background: '#221a0c', marginBottom: 16 }}>
          <div style={{ fontFamily: mono, fontSize: 12, color: '#F59E0B', marginBottom: 8 }}>HITL · {hitl.kind}</div>
          {hitl.kind === 'question' ? (
            <div>
              {((hitl.req.questions as Record<string, unknown>[]) ?? []).map((q, qi) => (
                <div key={qi} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 14, marginBottom: 6 }}>{String(q.question ?? q.header)}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {((q.options as Record<string, unknown>[]) ?? []).map((o, oi) => (
                      <button key={oi} onClick={() => reply(`/api/agent/questions/${hitl.req.id}/reply`, { sessionID: hitl.req.sessionID ?? sessionRef.current, answers: [[String(o.label)]] })}
                        style={{ fontFamily: mono, fontSize: 12, cursor: 'pointer', border: '1px solid #F59E0B', background: 'transparent', color: '#F59E0B', padding: '5px 12px', borderRadius: 5 }}>
                        {String(o.label)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, marginBottom: 8 }}>Allow <code>{String(hitl.req.permission)}</code> · patterns: {JSON.stringify(hitl.req.patterns)}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['once', 'always', 'reject'] as const).map(r => (
                  <button key={r} onClick={() => reply(`/api/agent/permissions/${hitl.req.id}/reply`, { sessionID: hitl.req.sessionID ?? sessionRef.current, reply: r })}
                    style={{ fontFamily: mono, fontSize: 12, cursor: 'pointer', border: `1px solid ${r === 'reject' ? '#EF4444' : '#22C55E'}`, background: 'transparent', color: r === 'reject' ? '#EF4444' : '#22C55E', padding: '5px 12px', borderRadius: 5 }}>
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <div style={{ fontFamily: mono, fontSize: 12, color: '#94A3B8', margin: '0 0 6px' }}>// assistant text (streaming)</div>
          <pre style={{ ...box, minHeight: 120, maxHeight: 260, whiteSpace: 'pre-wrap', fontFamily: mono, fontSize: 12, margin: 0 }}>{text || '—'}</pre>
          <div style={{ fontFamily: mono, fontSize: 12, color: '#94A3B8', margin: '16px 0 6px' }}>// AG-UI event stream</div>
          <pre style={{ ...box, minHeight: 120, maxHeight: 260, fontFamily: mono, fontSize: 11, color: '#64748B', margin: 0 }}>{log.join('\n') || '—'}</pre>
        </div>
        <div>
          <div style={{ fontFamily: mono, fontSize: 12, color: '#94A3B8', margin: '0 0 6px' }}>// tool calls — args stream in live (the "watch the agent write SQL" panel)</div>
          {Object.entries(tools).length === 0 && <pre style={{ ...box, minHeight: 120, margin: 0, color: '#64748B' }}>—</pre>}
          {Object.entries(tools).map(([id, t]) => (
            <div key={id} style={{ ...box, marginBottom: 10 }}>
              <div style={{ fontFamily: mono, fontSize: 12, color: '#38BDF8', marginBottom: 6 }}>▸ {t.name}</div>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: mono, fontSize: 12, color: '#F8FAFC' }}>{t.args || '…'}</pre>
              {t.result !== undefined && (
                <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', fontFamily: mono, fontSize: 11, color: '#22C55E', borderTop: '1px dashed #27344b', paddingTop: 6 }}>{t.result}</pre>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
